import * as supports from '@owl-nest/feature-detection'

type GetConfig<T, OPTIONNAL extends boolean | undefined> = {
  optional?: OPTIONNAL
  value: string | undefined
  parse?: (value: string) => T
  key: string
}

export function get<T = string>(config: GetConfig<T, undefined | false>): T
export function get<T = string>(config: GetConfig<T, true>): T | undefined
export function get<T = string>(config: GetConfig<T, boolean | undefined>): T | undefined {
  if (config.value === undefined && config.optional !== true) {
    throw new Error(`env variable "${config.key}" was not found`)
  }

  if (config.parse) {
    try {
      return config.value === undefined ? undefined : config.parse(config.value)
    } catch (e) {
      throw new Error(`Error while parsing env variable "${config.key}"`, { cause: e })
    }
  }

  return config.value as any
}

export function lazy<T>(getter: () => Promise<T>): Promise<T> {
  return new LazyPromise((res, rej) => {
    getter().then(res, rej)
  })
}

type Executor<T> = (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void

class LazyPromise<T> extends Promise<T> {
  #promise?: Promise<T>
  #executor: Executor<T>

  constructor(executor: Executor<T>) {
    super((res: any) => res())
    this.#executor = executor
  }

  then<TResult1 = T, TResult2 = never>(
    onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null,
  ): Promise<TResult1 | TResult2> {
    this.#promise = this.#promise ?? new Promise(this.#executor)
    return this.#promise.then(onfulfilled, onrejected)
  }

  catch<TResult = never>(
    onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null,
  ): Promise<T | TResult> {
    this.#promise = this.#promise ?? new Promise(this.#executor)
    return this.#promise.catch(onrejected)
  }
}

export function translateCountriesLabel(code: string, lang: string) {
  try {
    return supports.displayNames() ? new Intl.DisplayNames([lang], { type: 'region' }).of(code) : undefined
  } catch (error) {
    return undefined
  }
}
